import React from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ChartPieSimpleIcon } from '@revfluence/fresh-icons/solid/esm';
import { InstagramIcon, TiktokIcon, YoutubeIcon, PinterestIcon } from '@revfluence/fresh-icons/brands/esm';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Empty } from '@frontend/app/refresh-components';

import { GraphCardHeader, GraphCardTitle, GraphCardContent } from '../../../components/graph-card';

// Define the data interface we expect
interface PostTypeData {
  postType: string;
  tmv?: number | null;
}

interface TMVChartCardProps {
  className?: string;
  data?: PostTypeData[] | null | undefined;
}

interface TMVChartData {
  name: string;
  value: number;
  color: string;
}

// Color mapping for different post types (using lowercase keys for robust matching)
const POST_TYPE_COLORS: Record<string, string> = {
  // Instagram
  'instagram_post': '#062D1A',
  'instagram_reel': '#0C5A34',
  'instagram_story': '#13864E',
  'instagram_video': '#13864E',
  // TikTok
  'tiktok_video': '#4E7E7E',
  'tiktok_carousel': '#619E9E',
  'tiktok_post': '#4E7E7E',
  // YouTube
  'youtube_video': '#2E426D',
  'youtube_short': '#5B7FAF',
  // Pinterest
  'pinterest_pin': '#7FD4F4',
  'pinterest_post': '#7FD4F4',
  // Twitter/X
  'twitter_post': '#3996E0',
  'x_post': '#3996E0',
  // Facebook
  'facebook_post': '#70397C',
  // Blog
  'blog_post': '#EACD60',
  'blog_mention': '#EACD60',
  'blog_dedicated': '#EACD60',
};

// Human-readable display names for post types (using lowercase keys for robust matching)
const POST_TYPE_NAMES: Record<string, string> = {
  // Instagram
  'instagram_post': 'Instagram Post',
  'instagram_reel': 'Instagram Reel',
  'instagram_story': 'Instagram Story',
  'instagram_video': 'Instagram Video',
  // TikTok
  'tiktok_video': 'TikTok Video',
  'tiktok_carousel': 'TikTok Carousel',
  'tiktok_post': 'TikTok Post',
  // YouTube
  'youtube_video': 'YouTube Video',
  'youtube_short': 'YouTube Short',
  // Pinterest
  'pinterest_pin': 'Pinterest Pin',
  'pinterest_post': 'Pinterest Post',
  // Twitter/X
  'twitter_post': 'Twitter Post',
  'x_post': 'X Post',
  // Facebook
  'facebook_post': 'Facebook Post',
  // Blog
  'blog_post': 'Blog Post',
  'blog_mention': 'Blog Mention',
  'blog_dedicated': 'Blog Article',
};

// Helper function to get color for post type
const getPostTypeColor = (postType: string): string => {
  const normalizedType = postType.toLowerCase();
  return POST_TYPE_COLORS[normalizedType] || '#6B7280'; // fallback to gray
};

// Helper function to get human-readable name for post type
const getPostTypeName = (postType: string): string => {
  const normalizedType = postType.toLowerCase();
  return POST_TYPE_NAMES[normalizedType] || postType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const CustomLegend = ({ payload }: { payload?: TMVChartData[] }) => {
  const getPlatformIcon = (name: string) => {
    if (name.toLowerCase().includes('instagram')) {
      return <InstagramIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />;
    }
    if (name.toLowerCase().includes('tiktok')) {
      return <TiktokIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />;
    }
    if (name.toLowerCase().includes('youtube')) {
      return <YoutubeIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />;
    }
    if (name.toLowerCase().includes('pinterest')) {
      return <PinterestIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />;
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-2 justify-center h-full w-full">
      {payload?.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div
            className="flex w-6 h-8 sm:w-8 sm:h-12 justify-center items-center flex-shrink-0 rounded-[6px]"
            style={{ backgroundColor: entry.color }}
            aria-hidden="true"
          >
            {getPlatformIcon(entry.name)}
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-xs text-gray-600 truncate">{entry.name}</span>
            <span className="text-xs sm:text-lg font-semibold text-gray-900 truncate">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                notation: 'compact',
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
              }).format(entry.value)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const TMVChartCard = React.forwardRef<HTMLDivElement, TMVChartCardProps>(({ className, data }, ref) => {
  // Transform GraphQL data to chart format
  const transformPostTypesToChartData = (postTypes: PostTypeData[] | null | undefined): TMVChartData[] => {
    if (!postTypes) return [];

    return postTypes
      .filter((item) => item?.tmv && item.tmv > 0) // Only include items with TMV > 0
      .map((item) => ({
        name: getPostTypeName(item.postType),
        value: item.tmv || 0,
        color: getPostTypeColor(item.postType),
      }))
      .sort((a, b) => b.value - a.value); // Sort by TMV value descending
  };

  const chartData = transformPostTypesToChartData(data);
  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);
  const totalTMVData = React.useMemo(() => ({ name: 'Total TMV', value: totalValue, color: '' }), [totalValue]);
  const [hoveredData, setHoveredData] = React.useState<TMVChartData | typeof totalTMVData>(totalTMVData);
  const [isHovering, setIsHovering] = React.useState(false);

  // Reset hovered data when chart data changes
  React.useEffect(() => {
    setHoveredData(totalTMVData);
    setIsHovering(false);
  }, [totalTMVData]);

  return (
    <Card ref={ref} className={className} role="region" aria-label="Total Media Value breakdown by content type">
      <GraphCardHeader className="border-b">
        <GraphCardTitle>Total Media Value</GraphCardTitle>
      </GraphCardHeader>
      <GraphCardContent className="pt-4 flex flex-col justify-between flex-1">
        <div
          className="h-[250px] sm:h-[300px] lg:h-[320px] grid grid-cols-2 gap-4 overflow-hidden"
          role="graphics-document"
          aria-label={`Pie chart showing Total Media Value breakdown by content type. Total value: ${new Intl.NumberFormat(
            'en-US',
            {
              style: 'currency',
              currency: 'USD',
              notation: 'compact',
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            },
          ).format(totalValue)}`}
        >
          {/* Chart Column */}
          <div className="flex items-center justify-center min-h-0 h-full relative">
            <ResponsiveContainer width="100%" height="100%">
              {chartData.length === 0 ? (
                <Empty
                  icon={ChartPieSimpleIcon}
                  title="No TMV data available."
                  description="Total Media Value breakdown will appear here once available."
                  size="lg"
                />
              ) : (
                <PieChart style={{ cursor: 'pointer' }}>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="55%"
                    outerRadius="85%"
                    paddingAngle={2}
                    cornerRadius={4}
                    onMouseEnter={(_, index) => {
                      setHoveredData(chartData[index]);
                      setIsHovering(true);
                    }}
                    onMouseLeave={() => {
                      setHoveredData(totalTMVData);
                      setIsHovering(false);
                    }}
                  >
                    {chartData.map((entry, index) => {
                      const isActive = !isHovering || hoveredData.name === entry.name;
                      const cellColor = isActive ? entry.color : `${entry.color}66`; // Add 40% opacity for inactive cells
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={cellColor}
                          role="graphics-symbol"
                          tabIndex={0}
                          aria-label={`${entry.name}: ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            notation: 'compact',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 1,
                          }).format(entry.value)}`}
                          onFocus={() => {
                            setHoveredData(chartData[index]);
                            setIsHovering(true);
                          }}
                          onBlur={() => {
                            setHoveredData(totalTMVData);
                            setIsHovering(false);
                          }}
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setHoveredData(chartData[index]);
                              setIsHovering(true);
                            }
                          }}
                        />
                      );
                    })}
                  </Pie>
                </PieChart>
              )}
            </ResponsiveContainer>

            {/* Center hover content */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-xs text-gray-600 truncate mb-1">{hoveredData.name}</div>
                <div className="text-xs sm:text-lg font-semibold text-gray-900 truncate">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    notation: 'compact',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1,
                  }).format(hoveredData.value)}
                </div>
              </div>
            </div>
          </div>

          {/* Legend Column */}
          <div className="flex items-center justify-start flex-1 min-h-0 overflow-hidden">
            <CustomLegend payload={chartData} />
          </div>
        </div>

        {/* Screen reader accessible table */}
        <div className="sr-only">
          <table>
            <caption>Total Media Value breakdown by content type</caption>
            <thead>
              <tr>
                <th scope="col">Content Type</th>
                <th scope="col">Value</th>
                <th scope="col">Percentage</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((item) => (
                <tr key={item.name}>
                  <th scope="row">{item.name}</th>
                  <td>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      notation: 'compact',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 1,
                    }).format(item.value)}
                  </td>
                  <td>{totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(1) : '0'}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary for screen readers */}
        <div className="sr-only" aria-live="polite">
          <h3>Total Media Value Summary:</h3>
          <ul>
            <li>
              Total TMV:{' '}
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                notation: 'compact',
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
              }).format(totalValue)}
            </li>
            {chartData.length > 0 && (
              <li>
                Highest value content type:{' '}
                {chartData[0]?.name}
              </li>
            )}
          </ul>
        </div>
      </GraphCardContent>
    </Card>
  );
});

TMVChartCard.displayName = 'TMVChartCard';

export default TMVChartCard;
